import { useEffect, useRef } from 'react';

const SCRIPT_ID = 'cloudflare-turnstile-script';
const TEST_SITE_KEY = '1x00000000000000000000AA';

function loadTurnstile(): Promise<void> {
  if (window.turnstile) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const existing = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null;
    if (existing) {
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener('error', () => reject(new Error('Turnstile load failed')), { once: true });
      return;
    }
    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Turnstile load failed'));
    document.head.appendChild(script);
  });
}

export function Turnstile({ onToken, resetKey }: { onToken: (token: string) => void; resetKey: number }) {
  const container = useRef<HTMLDivElement>(null);
  const webEnv = import.meta.env as unknown as { VITE_TURNSTILE_SITE_KEY?: string; DEV: boolean };
  const siteKey = webEnv.VITE_TURNSTILE_SITE_KEY || (webEnv.DEV ? TEST_SITE_KEY : '');
  const usesTestKey = webEnv.DEV && siteKey === TEST_SITE_KEY;
  useEffect(() => {
    let widgetId: string | null = null;
    let active = true;
    if (!siteKey) return;
    if (usesTestKey) {
      onToken('test');
      return;
    }
    if (!container.current) return;
    void loadTurnstile().then(() => {
      if (!active || !container.current || !window.turnstile) return;
      widgetId = window.turnstile.render(container.current, {
        sitekey: siteKey,
        action: 'ocr_extract',
        theme: 'light',
        callback: onToken,
        'expired-callback': () => onToken(''),
        'error-callback': () => onToken(''),
      });
    });
    return () => { active = false; if (widgetId && window.turnstile) window.turnstile.remove(widgetId); };
  }, [onToken, resetKey, siteKey, usesTestKey]);
  if (!siteKey) return <p role="alert" className="text-sm text-red-700">ยังไม่ได้ตั้งค่า Turnstile site key</p>;
  if (usesTestKey) return <p className="text-center text-xs text-slate-500">Turnstile test mode</p>;
  return <div ref={container} className="flex min-h-16 justify-center" />;
}
